import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    PackageMinus, Building2, Eye, Package, Send, ChevronDown, Loader2, RefreshCw,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchOldStockCCList,
    fetchOldStockIndentList,
    fetchIndentType,
    fetchOldStockGrid,
    submitOldStockIssue,
    clearIndentList,
    clearIndentType,
    clearGrid,
    clearSubmitResult,
    resetOldStockIssue,
    selectOSICCList,
    selectOSIIndentList,
    selectOSIIndentType,
    selectOSIGridRows,
    selectOSIDepPercentList,
    selectOSISubmitResult,
    selectOSILoading,
    selectOSIErrors,
} from '../../slices/purchaseSlice/oldStockIssueSlice';

// ── Shared UI primitives (mirrors ItemsTransfer / CapitalStockIssue pages) ──

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const formatDateForSP = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// menuData carries an optional prefillCC/prefillIndentNo — used when navigated here straight
// from an Indent Creation verification (Approve at the Old Stock Issue level).
const OldStockIssue = ({ menuData } = {}) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || '';
    const createdBy = userData?.userName || userData?.employeeId || userData?.UID || 'system';

    const ccList         = useSelector(selectOSICCList);
    const indentList       = useSelector(selectOSIIndentList);
    const indentType         = useSelector(selectOSIIndentType);
    const gridRows             = useSelector(selectOSIGridRows);
    const depPercentList         = useSelector(selectOSIDepPercentList);
    const submitResult             = useSelector(selectOSISubmitResult);
    const loading                    = useSelector(selectOSILoading);
    const errors                      = useSelector(selectOSIErrors);

    const [selectedCC, setSelectedCC] = useState('');
    const [selectedIndent, setSelectedIndent] = useState('');

    const [checkedRows, setCheckedRows] = useState({});
    const [depEdits, setDepEdits] = useState({});

    const [issueDate, setIssueDate] = useState(new Date());
    const [tranDate, setTranDate] = useState(null);
    const [remarks, setRemarks] = useState('');

    const isAssetIndent = indentType === 'Asset';

    const prefillCCApplied = useRef(false);
    const prefillViewTriggered = useRef(false);

    useEffect(() => {
        if (roleId) dispatch(fetchOldStockCCList({ uid: userData?.UID || userData?.uid, rid: roleId }));
        return () => dispatch(resetOldStockIssue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Pre-fill from a verification-page redirect ──────────────────────────
    useEffect(() => {
        if (prefillCCApplied.current) return;
        if (!menuData?.prefillCC || ccList.length === 0) return;
        const match = ccList.find((c) => c.CCID === menuData.prefillCC);
        if (!match) return;
        prefillCCApplied.current = true;
        handleCCChange(menuData.prefillCC);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccList, menuData]);

    useEffect(() => {
        if (!menuData?.prefillIndentNo || indentList.length === 0 || !selectedCC || selectedIndent) return;
        const match = indentList.find((i) => i.Indentvalue === menuData.prefillIndentNo);
        if (!match) return;
        handleIndentChange(menuData.prefillIndentNo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indentList, menuData, selectedCC]);

    useEffect(() => {
        if (prefillViewTriggered.current) return;
        if (!menuData?.prefillIndentNo || selectedIndent !== menuData.prefillIndentNo) return;
        prefillViewTriggered.current = true;
        handleView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndent, menuData]);

    useEffect(() => {
        setCheckedRows({});
        setDepEdits({});
    }, [gridRows]);

    useEffect(() => {
        if (submitResult) {
            toast.success(`The Issue No Is :- ${submitResult.refNo}   Generated Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center / Indent selection ──────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setSelectedIndent('');
        dispatch(clearIndentList());
        dispatch(clearIndentType());
        dispatch(clearGrid());
        if (cc) dispatch(fetchOldStockIndentList({ ccode: cc }));
    };

    const handleIndentChange = (indent) => {
        setSelectedIndent(indent);
        dispatch(clearGrid());
        if (indent) dispatch(fetchIndentType({ indentno: indent }));
        else dispatch(clearIndentType());
    };

    const handleView = () => {
        if (!selectedCC) { toast.error('Please Select CC/Job Code'); return; }
        if (!selectedIndent) { toast.error('Select Indent No'); return; }
        dispatch(fetchOldStockGrid({ ccode: selectedCC, indent: selectedIndent }));
    };

    // ── Grid row edits ───────────────────────────────────────────────────────

    const toggleRowChecked = (rid) => setCheckedRows((prev) => ({ ...prev, [rid]: !prev[rid] }));

    const handleDepChange = (rid, value) => {
        setDepEdits((prev) => ({ ...prev, [rid]: value }));
    };

    // Mirrors legacy Calculate(): dep 100 ("FullValue") deducts nothing, otherwise the
    // "Depreciation Value" column is the deducted amount (Basic-before-dep × percent / 100).
    const depAmtFor = (row) => {
        const dep = depEdits[row.Rid];
        if (!dep) return null;
        if (num(dep) === 100) return 0;
        return num(row.BefDepAmt) * num(dep) / 100;
    };

    const sumBefDep = useMemo(() => gridRows.reduce((s, r) => s + num(r.BefDepAmt), 0), [gridRows]);
    const sumDepValue = useMemo(
        () => gridRows.reduce((s, r) => s + (depAmtFor(r) || 0), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridRows, depEdits]
    );

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleIssueDateChange = (d) => {
        setIssueDate(d);
        setTranDate(null);
    };

    const validateBeforeSubmit = () => {
        if (!selectedCC) { toast.error('Select CC/Job Code'); return false; }
        if (!selectedIndent) { toast.error('Select Indent No'); return false; }
        if (!issueDate) { toast.error('Select Date'); return false; }
        if (!tranDate) { toast.error('Select Expected Recieved Date'); return false; }
        if (!remarks.trim()) { toast.error('Select Remarks'); return false; }
        if (gridRows.length === 0) { toast.error('Please Add Items to Issue'); return false; }
        if (!gridRows.every((r) => checkedRows[r.Rid])) { toast.error('Please Verify Item Codes'); return false; }
        if (!isAssetIndent && !gridRows.every((r) => depEdits[r.Rid])) { toast.error('Select Depriciation Percent'); return false; }
        return true;
    };

    const handleSubmit = () => {
        if (!validateBeforeSubmit()) return;

        const rids = `${gridRows.map((r) => r.Rid).join(',')},`;
        const itemCodes = `${gridRows.map((r) => r.ItemCode).join(',')},`;

        let payload = {
            Rid: rids,
            ItemCode: itemCodes,
            Deppercent: '',
            ToCC: selectedCC,
            Issuedate: formatDateForSP(issueDate),
            Remarks: remarks,
            Createdby: createdBy,
            RoleID: String(roleId),
            BeforeDepAmt: '0',
            AfterDepAmt: '0',
            DepAmts: '',
            Indentno: selectedIndent,
            CheckIndentType: indentType,
            TransactionDate: formatDateForSP(tranDate),
        };

        if (!isAssetIndent) {
            const dep1 = gridRows.map((r) => (num(depEdits[r.Rid]) === 100 ? 'Full Value' : depEdits[r.Rid]));
            const depAmts = gridRows.map((r) => depAmtFor(r) ?? 0);
            payload = {
                ...payload,
                Deppercent: `${dep1.join(',')},`,
                DepAmts: `${depAmts.join(',')},`,
                BeforeDepAmt: sumBefDep.toFixed(2),
                AfterDepAmt: sumDepValue.toFixed(2),
            };
        }

        dispatch(submitOldStockIssue(payload));
    };

    const handleReset = () => {
        setSelectedCC('');
        setSelectedIndent('');
        setCheckedRows({});
        setDepEdits({});
        setIssueDate(new Date());
        setTranDate(null);
        setRemarks('');
        dispatch(clearIndentList());
        dispatch(clearIndentType());
        dispatch(clearGrid());
        if (roleId) dispatch(fetchOldStockCCList({ uid: userData?.UID || userData?.uid, rid: roleId }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = gridRows.length > 0;
    const checkedCount = gridRows.filter((r) => checkedRows[r.Rid]).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <PackageMinus className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Old Stock Issue</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Issue existing indent stock out to a cost center.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Cost Center + Indent selection ───────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center &amp; Indent" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Issue To Cost/Job Center</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.ccList}>
                                <option value="">-Please Select-</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>

                    {selectedCC && (
                        <div className="flex-1 min-w-[240px]">
                            <Label required>Indent No</Label>
                            <select className={inputCls} value={selectedIndent} onChange={(e) => handleIndentChange(e.target.value)} disabled={loading.indentList}>
                                <option value="">-Please Select-</option>
                                {indentList.map((i) => (
                                    <option key={i.Indentvalue} value={i.Indentvalue}>{i.Indenttext}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {selectedCC && (
                        <button onClick={handleView} disabled={loading.grid}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                            {loading.grid ? <SpinnerIcon /> : <Eye className="w-4 h-4" />} View
                        </button>
                    )}
                </div>
            </SectionCard>

            {/* ── Issue items grid ──────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Package} title="Issue Items List"
                    subtitle={`${checkedCount} of ${gridRows.length} item${gridRows.length !== 1 ? 's' : ''} verified`}
                    accent="indigo">
                    <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                    {(isAssetIndent
                                        ? ['Chk', 'Item Code', 'Item Name', 'Specification', 'Units', 'Basic', 'Quantity']
                                        : ['Chk', 'Item Code', 'Item Name', 'Specification', 'Units', 'Basic', 'Quantity', 'Before Depreciation', 'Depreciation', 'Depreciation Value']
                                    ).map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gridRows.map((row, idx) => {
                                    const depAmt = depAmtFor(row);
                                    return (
                                        <tr key={row.Rid ?? idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                            <td className="px-2.5 py-2">
                                                <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                    checked={!!checkedRows[row.Rid]} onChange={() => toggleRowChecked(row.Rid)} />
                                            </td>
                                            <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{row.ItemCode}</td>
                                            <td className="px-2.5 py-2 text-gray-900 dark:text-white">{row.ItemName}</td>
                                            <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{row.Specification}</td>
                                            <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{row.Units}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300">{row.Basic}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.IssueQuantity}</td>
                                            {!isAssetIndent && (
                                                <>
                                                    <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300">{num(row.BefDepAmt).toFixed(2)}</td>
                                                    <td className="px-1.5 py-1.5 w-28">
                                                        <select className={inputCls}
                                                            value={depEdits[row.Rid] || '0'}
                                                            onChange={(e) => handleDepChange(row.Rid, e.target.value === '0' ? '' : e.target.value)}>
                                                            <option value="0">Select</option>
                                                            {depPercentList.map((p) => (
                                                                <option key={p.Value} value={p.Value}>{p.Name}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2.5 py-2 text-right text-green-700 dark:text-green-400 font-semibold">
                                                        {depAmt !== null ? depAmt.toFixed(2) : '—'}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {!isAssetIndent && (
                                <tfoot>
                                    <tr className="bg-indigo-50/60 dark:bg-indigo-900/20 font-semibold text-gray-700 dark:text-gray-200">
                                        <td colSpan={7} className="px-2.5 py-2 text-right">Total</td>
                                        <td className="px-2.5 py-2 text-right text-green-700 dark:text-green-400">{sumBefDep.toFixed(2)}</td>
                                        <td></td>
                                        <td className="px-2.5 py-2 text-right text-green-700 dark:text-green-400">{sumDepValue.toFixed(2)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label required>Issue Date</Label>
                            <CustomDatePicker value={issueDate} onChange={handleIssueDateChange}
                                format="DD-MMM-YYYY" placeholder="Select Date" maxDate={new Date()} />
                        </div>
                        <div>
                            <Label required>Expected Received Date</Label>
                            <CustomDatePicker value={tranDate} onChange={setTranDate}
                                format="DD-MMM-YYYY" placeholder="Select Expected Received Date"
                                minDate={issueDate} maxDate={issueDate ? addDays(issueDate, 7) : null}
                                disabled={!issueDate} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default OldStockIssue;
